import { createContext, useContext, useEffect, useState } from "react";
import { UserCohearingSession } from "../../models/UserCohearingSession";
import { MuteReason, TelephonyContext } from "./TelephonyContext";
import { listCallCohearingSessions, listUserCohearingSessions } from "./service";
import { getUserId } from "../../utils/user";
import { AsynchronousNotificationContext } from "../../AsynchronousNotificationContextProvider";
import { AsynchronousNotification } from "../../models/AsynchronousNotification";
import { text } from "../../utils/i18n";
import { CallCohearingSession } from "../../models/CallCohearingSession";
import { CallConnectionState, CallFilters } from "../../models/CallFilters";
import { useCurrentUser } from "../user/UserDispatcher";

export const CohearingContextProvider: React.FC = ({ children }) => {
  const [activeUserSessions, setActiveUserSessions] = useState<UserCohearingSession[]>([]);
  const [callCohearingSessions, setCallCohearingSessions] = useState<CallCohearingSession[]>([]);
  const [isCallTakeover, setCallTakeover] = useState(false); // Om samtalet har tagits över av medlyssnare.
  const telephonyContext = useContext(TelephonyContext);
  const asyncNotifications = useContext(AsynchronousNotificationContext);
  const currentUser = useCurrentUser();

  const activeCallSession = telephonyContext.callSessions.find(session => session.isActive);

  //  Om antingen i ett samtal eller någonstans där man ser meddelanden.
  const isEnabled = Boolean(activeCallSession) ||
    telephonyContext.isTelephonyWidgetOpen ||
    telephonyContext.isView;

  //  Håll en uppdaterad med sessionen.
  const isFast = activeUserSessions.length > 0;

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const refreshId = setInterval(() => {
      if (!document.hidden) {
        listUserCohearingSessions({ isActive: true, userId: getUserId() })
          .then(sessions => setActiveUserSessions(sessions));
      }
    }, isFast ? 1500 : 7500);

    return () => { clearInterval(refreshId); };
  }, [isEnabled, isFast]);

  //  Detta kan ha varit förra samtalet.
  //  Vi behöver kolla om nuvarande samtalet har tagits över.
  const userTakeoverSessionExists = Boolean(activeUserSessions.find(session => session.takeoverAt));

  useEffect(() => {
    asyncNotifications.delete({ id: "CallTakeover" });
    setCallCohearingSessions([]);
  }, [activeCallSession?.sessionId]);

  useEffect(() => {
    if (!userTakeoverSessionExists || !activeCallSession) {
      return;
    }

    //  TODO: Det hade varit bättre att använda user-messages-v1 som en generell fallback
    //  för websocket. Kör på det istället.
    //  Tänk på att vissa messages ska hanteras av samtliga tabbar och vissa av en tabb (som notiser.)
    const refreshId = setInterval(() => {
      listCallCohearingSessions(new CallFilters({
        easyTelecomUserCallIds: activeCallSession.easyTelecomUserCallIds,
        userIds: [currentUser.id],
        connectionState: CallConnectionState.Active
      })).then(result => setCallCohearingSessions(result[0]?.cohearingSessions ?? []))
    }, 3500);

    return () => {
      clearInterval(refreshId)
    };
  }, [userTakeoverSessionExists, activeCallSession?.sessionId]);

  //  true ifall vi fått reda på takeover via polling istället för websocket.
  const isCallTakeoverViaPolling = Boolean(callCohearingSessions.find(session => session.takeoverAt));

  //  isCallTakeover kan även sättas via websocket.
  useEffect(() => {
    if (isCallTakeoverViaPolling && !isCallTakeover) {
      onCallTakeover();

    } else if (!isCallTakeoverViaPolling) {
      setCallTakeover(false);
    }
  }, [isCallTakeoverViaPolling]);

  useEffect(() => {
    if (!isCallTakeover && telephonyContext.muteReason === MuteReason.CallTakeover) {
      //  Sätt på mikrofonen när medlyssnaren har lämnat.
      telephonyContext.setState({ muteReason: null });
    }
  }, [isCallTakeover]);

  function onCallTakeover() {
    setCallTakeover(true);

    telephonyContext.setState({ muteReason: MuteReason.CallTakeover });

    //  Utan en notis kanske man inte inser att ens mikrofon är avstängd.
    asyncNotifications.notify(new AsynchronousNotification({
      icon: { name: "phone" },
      id: "CallTakeover",
      title: text`Samtalet har tagits över`,
      body: text`Mikrofonen är avstängd, men du kan sätta på den igen för att prata.`,
      actions: {
        confirm: {
          weight: "dimmed",
          children: text`Stäng`,
          onClick() {
            return true;
          }
        }
      }
    }));
  }

  return (
    <CohearingContext.Provider value={{
      activeUserSessions,
      onCallTakeover
    }}>
      {children}
    </CohearingContext.Provider>
  )
}

export const CohearingContext = createContext<{
  /**
   * Active co-hearing sessions where someone else listens on the current user.
   */
  activeUserSessions: UserCohearingSession[];

  /**
   * Called when the call has been taken over by a co-listener.
   */
  onCallTakeover: () => void;
}>(null);